import { useEffect, useRef } from 'react'
import { LuX, LuPoundSterling, LuBed, LuBath, LuRuler, LuMapPin, LuHouse, LuKey } from 'react-icons/lu'
import { formatArea, formatPriceWithQualifier } from '../utils/formatters.js'

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  return String(value).replace(/_/g, ' ')
}

const rows = [
  {
    label: 'Price',
    icon: LuPoundSterling,
    render: (p) => formatPriceWithQualifier(p.price, p.priceQualifier),
  },
  {
    label: 'Bedrooms',
    icon: LuBed,
    render: (p) => (p.beds != null ? `${p.beds} bed${p.beds !== 1 ? 's' : ''}` : '—'),
  },
  {
    label: 'Bathrooms',
    icon: LuBath,
    render: (p) => (p.baths != null ? `${p.baths} bath${p.baths !== 1 ? 's' : ''}` : '—'),
  },
  {
    label: 'Floor area',
    icon: LuRuler,
    render: (p) => (p.area ? formatArea(p.area) : '—'),
  },
  {
    label: 'Location',
    icon: LuMapPin,
    render: (p) => [p.location, p.city].filter(Boolean).join(', ') || '—',
  },
  {
    label: 'Property type',
    icon: LuHouse,
    render: (p) => formatValue(p.propertyType),
  },
  {
    label: 'Tenure',
    icon: LuKey,
    render: (p) => formatValue(p.tenure),
  },
]

const ComparisonModal = ({ properties, onClose }) => {
  const dialogRef = useRef(null)

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [onClose])

  const handleBackdropClick = (e) => {
    if (e.target === dialogRef.current) onClose()
  }

  return (
    <div
      className="comparison-modal__backdrop"
      ref={dialogRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Full property comparison"
    >
      <div className="comparison-modal">
        <div className="comparison-modal__header">
          <h2 className="comparison-modal__title">Property Comparison</h2>
          <button
            type="button"
            className="comparison-modal__close"
            aria-label="Close comparison"
            onClick={onClose}
          >
            <LuX aria-hidden="true" />
          </button>
        </div>

        <div className="comparison-modal__body">
          <div className="comparison-modal__scroll">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th className="comparison-table__label-col" scope="col"></th>
                  {properties.map((p) => (
                    <th key={p.id} scope="col" className="comparison-table__property-col">
                      <div className="comparison-table__property-header">
                        <img
                          src={p.heroImage}
                          alt={p.title}
                          className="comparison-table__hero"
                        />
                        <span className="comparison-table__property-title">{p.title}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label} className="comparison-table__row">
                    <th scope="row" className="comparison-table__row-label" title={row.label} aria-label={row.label}>
                      <row.icon aria-hidden="true" />
                    </th>
                    {properties.map((p) => (
                      <td key={p.id} className="comparison-table__cell">
                        {row.render(p)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ComparisonModal
